import {
  FieldValidation,
  FieldValidationStatus,
  getValidationStatusColor,
} from "@/types/validation";
import { useState } from "react";
import { ValidationPopover } from "./ValidationPopover";

interface ValidationIconProps {
  /** Field name for display */
  fieldName: string;

  /** Validation metadata */
  validation: FieldValidation;

  /** Current field value */
  currentValue: string | number | boolean | null;

  /** Confidence score (0-1) */
  confidence: number;
}

/**
 * Validation icon component that displays status and shows popover on hover/click
 */
export function ValidationIcon({
  fieldName,
  validation,
  currentValue,
  confidence,
}: ValidationIconProps) {
  const [isOpen, setIsOpen] = useState(false);
  const colors = getValidationStatusColor(validation.status);

  const getIcon = (status: FieldValidationStatus) => {
    switch (status) {
      case "auto_corrected":
        return (
          <svg
            className={`w-4 h-4 ${colors.icon}`}
            fill="currentColor"
            viewBox="0 0 20 20"
            aria-label="Auto-corrected"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
        );
      case "suggested_add":
      case "suggested_update":
      case "suggested_review":
        return (
          <svg
            className={`w-4 h-4 ${colors.icon}`}
            fill="currentColor"
            viewBox="0 0 20 20"
            aria-label="Suggested correction"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
        );
      case "flagged":
        return (
          <svg
            className={`w-4 h-4 ${colors.icon}`}
            fill="currentColor"
            viewBox="0 0 20 20"
            aria-label="Flagged for attention"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        );
      case "requires_review":
        return (
          <svg
            className={`w-4 h-4 ${colors.icon}`}
            fill="currentColor"
            viewBox="0 0 20 20"
            aria-label="Requires review"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
        );
      case "validated":
        return (
          <svg
            className={`w-4 h-4 ${colors.icon}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-label="Validated"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
    }
  };

  return (
    <div className="relative inline-block">
      <button
        type="button"
        className="inline-flex items-center justify-center hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary rounded"
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        aria-label={`View validation details for ${fieldName}`}
      >
        {getIcon(validation.status)}
      </button>

      {isOpen && (
        <ValidationPopover
          fieldName={fieldName}
          validation={validation}
          currentValue={currentValue}
          confidence={confidence}
          onClose={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}
